import { useAuthStore } from '@/store/auth';
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { attributionLinkApi } from '@/lib/finance-channel-api';

// Linki pomiarowe sklepu. Meta Ads ma link uniwersalny (jeden dla wszystkich
// reklam, bez wybierania kampanii); e-mail wymaga wskazania wiadomości,
// bo link musi istnieć w jej treści (dowód miejsca publikacji).
export function FinanceAttributionLinkForm({ storeId, brandId }: { storeId: string; brandId: string }) {
  const tenantId = useAuthStore(state => state.tenant?.id);
  const userId = useAuthStore(state => state.user?.id);
  const [channel, setChannel] = useState<'META_ADS' | 'EMAIL'>('META_ADS');
  const [selection, setSelection] = useState('');
  const [path, setPath] = useState('/');
  const [showExpired, setShowExpired] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const listKey = ['attribution-links', tenantId, userId, storeId, brandId];

  const links = useQuery({
    queryKey: listKey,
    queryFn: async () => (await attributionLinkApi.list(storeId, brandId)).data,
    retry: false,
  });
  const options = useQuery({
    queryKey: ['attribution-link-options', tenantId, userId, storeId, brandId],
    queryFn: async () => (await attributionLinkApi.options(storeId, brandId)).data,
    retry: false,
    enabled: channel === 'EMAIL',
  });

  const create = useMutation({
    mutationFn: async () => {
      const from = new Date();
      const message = channel === 'EMAIL' ? options.data?.messages[Number(selection)] : undefined;
      if (channel === 'EMAIL' && (selection === '' || !message)) throw new Error('SELECTION_REQUIRED');
      const issued = (await attributionLinkApi.issue(storeId, {
        brandId, channel,
        messageVersionId: message?.messageVersionId ?? null,
        adMappingId: null, campaignId: null,
        destinationPath: channel === 'EMAIL' ? message!.destinationPath : path,
        validFrom: from.toISOString(),
        validThrough: '2099-12-31T23:59:59.000Z',
      })).data;
      await attributionLinkApi.install(storeId, brandId, issued.linkRef);
      return issued;
    },
    onSuccess: () => { void links.refetch(); },
  });

  const revoke = useMutation({
    mutationFn: async (linkRef: string) => (await attributionLinkApi.revoke(storeId, brandId, linkRef)).data,
    onSuccess: () => { setConfirmRevoke(null); void links.refetch(); },
  });

  const fmtDate = (iso: string) => new Date(iso).getFullYear() >= 2099 ? 'bezterminowo' : new Date(iso).toLocaleDateString('pl-PL');

  // Wycofane i wygasłe linki (validThrough w przeszłości) są domyślnie ukryte,
  // żeby lista pokazywała tylko działające linki kampanii.
  const now = Date.now();
  const allLinks = links.data ?? [];
  const expiredCount = allLinks.filter(link => Date.parse(link.validThrough) <= now).length;
  const visibleLinks = allLinks.filter(link => showExpired || Date.parse(link.validThrough) > now);

  return (
    <div className="space-y-3 border-t border-border pt-4">
      <h3 className="font-semibold">Linki kampanii</h3>
      <p className="text-sm text-text-secondary">
        Link pomiarowy wklejasz jako docelowy adres reklamy lub wiadomości - dzięki niemu system rozpoznaje,
        z którego kanału przyszedł klient. Link Meta jest uniwersalny: jeden dla wszystkich reklam Meta tego
        sklepu, bez wybierania kampanii. Link jest bezterminowy - kampania nigdy nie straci pomiaru.
        Kanał jest rozpoznawany po linku, więc Meta i e-mail mają osobne linki.
      </p>

      {links.isSuccess && allLinks.length > 0 && (
        <div className="space-y-2">
          {expiredCount > 0 && (
            <button type="button" className="text-xs text-text-secondary underline hover:text-accent-primary" onClick={() => setShowExpired(v => !v)}>
              {showExpired ? 'Ukryj wygasłe' : `Pokaż wygasłe (${expiredCount})`}
            </button>
          )}
          {visibleLinks.map(link => {
            const expired = Date.parse(link.validThrough) <= now;
            return (
              <div key={link.linkRef} className="p-3 bg-surface-hover/30 rounded-lg space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs text-text-secondary">
                    <span className="text-text-primary font-medium">{link.channel === 'META_ADS' ? 'Meta Ads' : 'Email'}</span>
                    {' · '}strona {link.destinationPath} · {expired
                      ? `nieważny od ${new Date(link.validThrough).toLocaleDateString('pl-PL')}`
                      : new Date(link.validThrough).getFullYear() >= 2099 ? 'ważny bezterminowo' : `ważny do ${fmtDate(link.validThrough)}`}
                    {link.campaignBound && ' · przypisany do kampanii'}
                  </p>
                  {!expired && (
                    <Button variant="ghost" size="sm" disabled={revoke.isPending} onClick={() => { revoke.reset(); setConfirmRevoke(link.linkRef); }}>
                      Wycofaj
                    </Button>
                  )}
                </div>
                {link.url ? (
                  <input readOnly className="input w-full text-xs" value={link.url} onFocus={e => e.currentTarget.select()} />
                ) : (
                  <p className="text-xs text-text-muted">Link utworzony wcześniej - pełny adres był widoczny tylko przy jego tworzeniu.</p>
                )}
              </div>
            );
          })}
          {revoke.isError && (
            <p role="alert" className="text-sm text-warning">
              {(revoke.error as { response?: { data?: { error?: string } } })?.response?.data?.error
                || 'Nie udało się wycofać linku. Sprawdź stan pomiaru i spróbuj ponownie.'}
            </p>
          )}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="space-y-1 text-sm">
          <span>Kanał</span>
          <Select value={channel} disabled={create.isPending} onChange={e => { setChannel(e.target.value as typeof channel); setSelection(''); create.reset(); }}>
            <option value="META_ADS">Meta Ads</option>
            <option value="EMAIL">Email</option>
          </Select>
        </label>
        {channel === 'META_ADS' && (
          <label className="space-y-1 text-sm sm:col-span-2">
            <span>Strona docelowa w sklepie</span>
            <input className="input w-full" value={path} placeholder="/" disabled={create.isPending} onChange={e => { setPath(e.target.value); create.reset(); }} />
          </label>
        )}
        {channel === 'EMAIL' && (
          <label className="space-y-1 text-sm sm:col-span-2">
            <span>Wersja wiadomości i link</span>
            <Select value={selection} disabled={create.isPending || options.isPending} onChange={e => { setSelection(e.target.value); create.reset(); }}>
              <option value="">Wybierz</option>
              {options.data?.messages.map((m, i) => (
                <option key={`${m.messageVersionId}:${i}`} value={String(i)}>Wersja {m.versionNumber} · {m.destinationPath}</option>
              ))}
            </Select>
          </label>
        )}
      </div>
      {channel === 'EMAIL' && options.isSuccess && !options.data.messages.length && (
        <p className="text-sm text-text-muted">Brak zapisanej wiadomości z linkiem prowadzącym do tego sklepu.</p>
      )}
      {channel === 'EMAIL' && options.isError && (
        <p role="alert" className="text-sm text-warning">Nie udało się odczytać wiadomości. Sprawdź powiązania sklepu.</p>
      )}

      <Button
        loading={create.isPending}
        disabled={channel === 'EMAIL' && selection === ''}
        onClick={() => create.mutate()}
      >
        {channel === 'META_ADS' ? 'Utwórz uniwersalny link Meta' : 'Utwórz link e-mail'}
      </Button>
      {create.isError && (
        <p role="alert" className="text-sm text-warning">
          {(create.error as { response?: { data?: { error?: string } } })?.response?.data?.error
            || 'Nie udało się przygotować linku w sklepie. Sprawdź wybór i stan pomiaru, a następnie ponów.'}
        </p>
      )}
      {create.isSuccess && (
        <p role="status" className="text-sm text-success">Link gotowy i przekazany do sklepu - skopiuj go z listy powyżej do reklamy lub wiadomości.</p>
      )}

      <ConfirmDialog
        isOpen={confirmRevoke !== null}
        subtitle={null}
        danger
        title="Wycofać link kampanii?"
        message="Link przestanie działać natychmiast - nowe wejścia przez niego nie będą już przypisywane do kampanii. Zebrane dotąd dane zostają."
        confirmLabel="Wycofaj"
        isPending={revoke.isPending}
        onConfirm={() => { if (confirmRevoke) revoke.mutate(confirmRevoke); }}
        onClose={() => { setConfirmRevoke(null); revoke.reset(); }}
      />
    </div>
  );
}
